import { useState } from 'react';
import { Box, Button, IconButton, Typography, Avatar, Tooltip, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReplayIcon from '@mui/icons-material/Replay';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { colors } from '@src/styles';
import { fadeInSX } from '@src/styles/animations';
import { CustomJsonViewer } from '@src/components';
import { TTaskStatus } from '../hooks/dto/taskItem.dto';
import { useSnackbar } from 'notistack';
import { CommentsSection } from './CommentsSection';
import { useTasksContext } from '../context/TasksContext';
import { statusColors, statusLabels } from './StatusBar';

const titleFontFamily = "'Inter Tight', 'Inter', sans-serif";
const monoFontFamily = "'JetBrains Mono', monospace";

const monoLabelSX = {
  fontFamily: monoFontFamily,
  fontSize: '10px',
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: colors.brand.textMuted,
} as const;

type TTaskTypeTone = { label: string; color: string; icon: React.ReactNode };

const getTaskTypeTone = (type?: string): TTaskTypeTone => {
  const upperType = (type ?? '').toUpperCase();
  if (upperType.includes('ERROR') || upperType.includes('ERRO')) {
    return { label: 'Error', color: colors.status.rejected, icon: <ErrorIcon sx={{ fontSize: 14 }} /> };
  }
  if (upperType.includes('WARNING') || upperType.includes('AVISO') || upperType.includes('ALERTA')) {
    return { label: 'Warning', color: colors.status.pending, icon: <WarningIcon sx={{ fontSize: 14 }} /> };
  }
  return { label: type || 'Info', color: colors.primary.blue, icon: <InfoIcon sx={{ fontSize: 14 }} /> };
};

const formatDateTime = (value: string): string => format(new Date(value), 'dd/MM/yyyy · HH:mm');

const getInitials = (name: string): string => {
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const TonePill = ({ label, color, icon }: { label: string; color: string; icon?: React.ReactNode }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      height: 20,
      paddingX: 1,
      borderRadius: '999px',
      border: `1px solid ${color}55`,
      backgroundColor: `${color}14`,
      color,
      fontFamily: monoFontFamily,
      fontSize: '9.5px',
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
    }}
  >
    {icon}
    {label}
  </Box>
);

const StatusPill = ({ status }: { status: TTaskStatus | undefined }) => {
  const color = (status && statusColors[status]) || colors.brand.textSecondary;
  return (
    <TonePill
      label={(status && statusLabels[status]) || status || 'Desconhecido'}
      color={color}
      icon={<Box sx={{ width: 6, height: 6, borderRadius: '999px', backgroundColor: color }} />}
    />
  );
};

const PropertyRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      minHeight: 34,
      paddingX: 1.5,
      paddingY: 0.5,
      borderBottom: `1px solid ${colors.brand.line}`,
      '&:last-of-type': { borderBottom: 'none' },
      minWidth: 0,
    }}
  >
    <Typography component="dt" sx={{ ...monoLabelSX, width: 100, flexShrink: 0 }}>
      {label}
    </Typography>
    <Box
      component="dd"
      sx={{
        margin: 0,
        flex: 1,
        minWidth: 0,
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 1,
        color: colors.primary.white,
        fontSize: '12.5px',
      }}
    >
      {children}
    </Box>
  </Box>
);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography sx={{ ...monoLabelSX, marginBottom: 1 }}>{children}</Typography>
);

const ghostButtonSX = {
  height: 30,
  paddingX: 1.25,
  gap: 0.75,
  borderRadius: '8px',
  border: `1px solid ${colors.brand.line}`,
  backgroundColor: 'transparent',
  color: colors.brand.textStrong,
  fontFamily: titleFontFamily,
  fontSize: '12.5px',
  fontWeight: 500,
  textTransform: 'none',
  whiteSpace: 'nowrap',
  '&:hover': { borderColor: colors.primary.yellow, color: colors.primary.white, backgroundColor: 'transparent' },
  '&:disabled': { opacity: 0.5, color: colors.brand.textMuted },
} as const;

const iconButtonSX = {
  width: 28,
  height: 28,
  color: colors.brand.textSecondary,
  '&:hover': { color: colors.primary.white, backgroundColor: 'rgba(255, 160, 21, 0.1)' },
} as const;

export const TaskDetailsPanel = () => {
  const {
    selectedTask,
    isLoadingTask,
    userNames,
    handleUpdateTaskUser,
    handleCreateRerun,
    taskContext,
    isLoadingTaskContext,
    clearSelectedTask,
  } = useTasksContext();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [activeTab, setActiveTab] = useState<'details' | 'comments'>('details');
  const [isRerunLoading, setIsRerunLoading] = useState(false);

  const panelSX = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    minHeight: 0,
    backgroundColor: colors.brand.surface,
    border: `1px solid ${colors.brand.line}`,
    borderRadius: '14px',
    overflow: 'hidden',
  } as const;

  if (isLoadingTask) {
    return (
      <Box sx={{ ...panelSX, alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={28} thickness={2} sx={{ color: colors.primary.yellow }} />
      </Box>
    );
  }

  if (!selectedTask) return null;

  const typeTone = getTaskTypeTone(selectedTask.type);
  const details = (selectedTask.details ?? {}) as Record<string, unknown>;
  const link = typeof details.link === 'string' ? details.link : undefined;
  const requestId = typeof details.requestId === 'string' ? details.requestId : undefined;
  const errorMessage =
    details.errorMessage === undefined || details.errorMessage === null || details.errorMessage === ''
      ? undefined
      : typeof details.errorMessage === 'string'
        ? details.errorMessage
        : JSON.stringify(details.errorMessage, null, 2);
  const runContext = taskContext?.run != null && 'context' in taskContext.run ? taskContext.run.context : undefined;
  const isFinished = ['CONCLUDED', 'REJECTED'].includes(selectedTask.status ?? '');
  const isAutomation = selectedTask.source === 'AUTOMATIONS';
  const assignedName = selectedTask.assignedUserId
    ? userNames[selectedTask.assignedUserId] || selectedTask.assignedUserId
    : null;
  const commentsCount = selectedTask.comments?.length || 0;

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    enqueueSnackbar('Copiado para a área de transferência', { variant: 'success', autoHideDuration: 1000 });
  };

  const handleRerunClick = async () => {
    try {
      setIsRerunLoading(true);
      await handleCreateRerun();
      enqueueSnackbar('Rerun criado com sucesso', { variant: 'success', autoHideDuration: 2000 });
    } catch {
      enqueueSnackbar('Erro ao criar rerun', { variant: 'error', autoHideDuration: 2000 });
    } finally {
      setIsRerunLoading(false);
    }
  };

  const handleOpenRun = () => {
    navigate(requestId ? `/runs?runId=${requestId}` : '/runs');
  };

  const tabs: { id: 'details' | 'comments'; label: string }[] = [
    { id: 'details', label: 'Detalhes' },
    { id: 'comments', label: `Comentários${commentsCount ? ` · ${commentsCount}` : ''}` },
  ];

  return (
    <Box sx={panelSX}>
      <Box
        sx={{
          padding: '14px 16px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
          flexShrink: 0,
          borderBottom: `1px solid ${colors.brand.line}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <TonePill label={typeTone.label} color={typeTone.color} icon={typeTone.icon} />
          <StatusPill status={selectedTask.status} />
          {selectedTask.source && <TonePill label={selectedTask.source} color={colors.brand.textSecondary} />}
          <IconButton
            onClick={clearSelectedTask}
            aria-label="Fechar detalhes da task"
            size="small"
            sx={{ ...iconButtonSX, width: 28, height: 28, marginLeft: 'auto' }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <Typography
          component="h2"
          sx={{
            fontFamily: titleFontFamily,
            fontSize: '15px',
            fontWeight: 600,
            lineHeight: 1.35,
            letterSpacing: '-0.01em',
            color: colors.primary.white,
            wordBreak: 'break-word',
          }}
        >
          {selectedTask.message}
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button
            onClick={() => handleUpdateTaskUser(selectedTask.id, null, true)}
            startIcon={<PersonAddAlt1Icon sx={{ fontSize: 16 }} />}
            sx={{
              ...ghostButtonSX,
              borderColor: 'rgba(255, 160, 21, 0.5)',
              backgroundColor: 'rgba(255, 160, 21, 0.1)',
              color: colors.primary.yellow,
              '&:hover': { backgroundColor: 'rgba(255, 160, 21, 0.18)', borderColor: colors.primary.yellow },
            }}
          >
            Atribuir a mim
          </Button>
          {isAutomation && (
            <Button
              onClick={handleRerunClick}
              disabled={isRerunLoading}
              startIcon={
                isRerunLoading ? (
                  <CircularProgress size={14} sx={{ color: colors.primary.yellow }} />
                ) : (
                  <ReplayIcon sx={{ fontSize: 16 }} />
                )
              }
              sx={ghostButtonSX}
            >
              Criar rerun
            </Button>
          )}
          {isAutomation && (
            <Button onClick={handleOpenRun} startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />} sx={ghostButtonSX}>
              Ver run completo
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ paddingX: 2, paddingTop: 1.5, flexShrink: 0 }}>
        <Box
          role="tablist"
          aria-label="Seções da task"
          sx={{
            display: 'inline-flex',
            padding: '3px',
            borderRadius: '10px',
            backgroundColor: colors.background.primary,
            border: `1px solid ${colors.brand.line}`,
          }}
        >
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <Box
                key={tab.id}
                component="button"
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(tab.id)}
                sx={{
                  height: 28,
                  paddingX: 1.5,
                  border: 'none',
                  borderRadius: '7px',
                  cursor: 'pointer',
                  fontFamily: titleFontFamily,
                  fontSize: '12.5px',
                  fontWeight: isActive ? 600 : 500,
                  backgroundColor: isActive ? colors.brand.surface : 'transparent',
                  boxShadow: isActive ? `inset 0 0 0 1px ${colors.brand.line}` : 'none',
                  color: isActive ? colors.primary.white : colors.brand.textSecondary,
                  transition: 'color 0.15s ease, background-color 0.15s ease',
                  '&:hover': { color: colors.primary.white },
                }}
              >
                {tab.label}
              </Box>
            );
          })}
        </Box>
      </Box>

      {activeTab === 'details' ? (
        <Box
          key={`details-${selectedTask.id}`}
          sx={{
            ...fadeInSX(),
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            padding: '12px 16px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box>
            <SectionTitle>Informações</SectionTitle>
            <Box
              component="dl"
              sx={{
                margin: 0,
                borderRadius: '10px',
                border: `1px solid ${colors.brand.line}`,
                backgroundColor: colors.background.primary,
                overflow: 'hidden',
              }}
            >
              <PropertyRow label="Responsável">
                {assignedName ? (
                  <>
                    <Avatar
                      sx={{
                        width: 22,
                        height: 22,
                        fontSize: '10px',
                        fontWeight: 600,
                        backgroundColor: colors.brand.indigo,
                        color: colors.primary.white,
                      }}
                    >
                      {getInitials(assignedName)}
                    </Avatar>
                    <Typography sx={{ fontSize: '13px', color: colors.primary.white }} noWrap>
                      {assignedName}
                    </Typography>
                  </>
                ) : (
                  <Typography sx={{ fontSize: '13px', color: colors.brand.textMuted }}>Não atribuído</Typography>
                )}
              </PropertyRow>

              <PropertyRow label="Criado">
                <Typography sx={{ fontFamily: monoFontFamily, fontSize: '12px', color: colors.brand.textStrong }}>
                  {formatDateTime(selectedTask.createdAt)}
                </Typography>
              </PropertyRow>

              {isFinished && selectedTask.completedAt && (
                <PropertyRow label="Concluído">
                  <CheckCircleIcon sx={{ fontSize: 14, color: colors.status.concluded }} />
                  <Typography sx={{ fontFamily: monoFontFamily, fontSize: '12px', color: colors.brand.textStrong }}>
                    {formatDateTime(selectedTask.completedAt)}
                  </Typography>
                </PropertyRow>
              )}

              {isFinished && selectedTask.completedByUserId && (
                <PropertyRow label="Concluído por">
                  <Typography sx={{ fontSize: '13px', color: colors.primary.white }} noWrap>
                    {userNames[selectedTask.completedByUserId] || selectedTask.completedByUserId}
                  </Typography>
                </PropertyRow>
              )}

              {requestId && (
                <PropertyRow label="Request ID">
                  <Tooltip title={requestId} arrow>
                    <Typography
                      sx={{ fontFamily: monoFontFamily, fontSize: '12px', color: colors.brand.textStrong, minWidth: 0 }}
                      noWrap
                    >
                      {requestId}
                    </Typography>
                  </Tooltip>
                  <Tooltip title="Copiar request ID" arrow>
                    <IconButton
                      size="small"
                      aria-label="Copiar request ID"
                      onClick={() => handleCopy(requestId)}
                      sx={iconButtonSX}
                    >
                      <ContentCopyIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                </PropertyRow>
              )}

              {link && (
                <PropertyRow label="Link">
                  <Tooltip title={link} arrow>
                    <Typography
                      component="a"
                      href={link}
                      target="_blank"
                      rel="noreferrer"
                      sx={{ fontSize: '13px', color: colors.primary.yellow, minWidth: 0, textDecoration: 'none' }}
                      noWrap
                    >
                      {link}
                    </Typography>
                  </Tooltip>
                  <OpenInNewIcon sx={{ fontSize: 14, color: colors.brand.textMuted, flexShrink: 0 }} />
                </PropertyRow>
              )}
            </Box>
          </Box>

          {(selectedTask.details || isLoadingTaskContext || runContext) && (
            <Box>
              <SectionTitle>Dados da execução</SectionTitle>
              <Box
                component="dl"
                sx={{
                  margin: 0,
                  borderRadius: '10px',
                  border: `1px solid ${colors.brand.line}`,
                  backgroundColor: colors.background.primary,
                  overflow: 'hidden',
                }}
              >
                {selectedTask.details && (
                  <PropertyRow label="Detalhes">
                    <CustomJsonViewer data={selectedTask.details} />
                  </PropertyRow>
                )}
                {isLoadingTaskContext ? (
                  <PropertyRow label="Contexto">
                    <CircularProgress size={14} sx={{ color: colors.primary.yellow }} />
                  </PropertyRow>
                ) : (
                  runContext && (
                    <PropertyRow label="Contexto">
                      <CustomJsonViewer data={runContext} />
                    </PropertyRow>
                  )
                )}
              </Box>
            </Box>
          )}

          {errorMessage && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
                <Typography sx={{ ...monoLabelSX, color: colors.status.rejected, flexGrow: 1 }}>
                  Mensagem de erro
                </Typography>
                <Tooltip title="Copiar mensagem" arrow>
                  <IconButton
                    size="small"
                    aria-label="Copiar mensagem de erro"
                    onClick={() => handleCopy(errorMessage)}
                    sx={iconButtonSX}
                  >
                    <ContentCopyIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box
                component="pre"
                sx={{
                  margin: 0,
                  maxHeight: 240,
                  overflowY: 'auto',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: `1px solid ${colors.status.rejected}40`,
                  backgroundColor: `${colors.status.rejected}0D`,
                  fontFamily: monoFontFamily,
                  fontSize: '11.5px',
                  lineHeight: 1.6,
                  color: colors.brand.textStrong,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {errorMessage}
              </Box>
            </Box>
          )}
        </Box>
      ) : (
        <Box
          key={`comments-${selectedTask.id}`}
          sx={{ ...fadeInSX(), flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 16px 16px' }}
        >
          <CommentsSection comments={selectedTask.comments || []} />
        </Box>
      )}
    </Box>
  );
};
